// 05-11 (auditoría, QA M4 + flujo #1/#7) — Alt+↓ / Alt+↑ «siguiente que te necesita», en UN sitio para toda la oficina.
//
// Antes el atajo vivía en el chat (useTeclasChat) y, sin chat, solo con la lista filtrada (ListaEquipo): la cabecera de
// la lista enseña siempre «siguiente Alt ↓», pero en la oficina recién abierta no pasaba nada. Y tras filtrar con «Ver»
// el foco se quedaba en su botón y Radix se comía la flecha (reabría el menú). Ahora un único oyente, en la fase de
// captura (antes que el botón que tenga el foco), para la página entera:
// - con un chat abierto: el siguiente (o anterior) que te necesita en el orden de la lista, dando la vuelta; con Mayús,
//   el siguiente sin más. Si no queda nadie, lo dice.
// - sin chat: el primero que te necesita (Alt+↑: el último); con la lista filtrada y alguien esperando fuera del filtro,
//   Alt+↓ va a ese (es lo que promete la pastilla «N te esperan fuera · Alt ↓») y quita el filtro.
// El orden es el de useEquipo: el que se ve en la lista (con su foto si está quieta, ordenQuieto.ts).
// No actúa con un diálogo o un menú abierto encima (sus flechas son suyas) ni escribiendo en un campo de una línea; en la
// caja del chat sí (es el bucle: contestar, Alt+↓, contestar).
import { useEffect, useRef } from 'react';
import { t } from '../../../i18n';
import { useToastStore } from '../../../stores/toast.store';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { useOficinaStore } from '../store';
import { hayDialogoAbierto } from '../ui/useEscape';
import { enFiltro } from './filtroGrupo';
import { siguienteEnEquipo, useEquipo } from './useEquipo';

export function useSiguiente({ contratados, abierto, alAbrir }: { contratados: Record<string, ContratadoDTO>; abierto: string | null; alAbrir: (agentId: string) => void }): void {
  const { orden, necesita } = useEquipo(contratados);
  const ultimo = useRef({ orden, necesita, abierto, alAbrir });
  ultimo.current = { orden, necesita, abierto, alAbrir };
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') || e.defaultPrevented) return;
      if (useOficinaStore.getState().contratarAbierto || hayDialogoAbierto()) return;
      const destino = e.target as HTMLElement | null;
      if (destino && (destino.isContentEditable || destino.tagName === 'INPUT' || destino.tagName === 'SELECT')) return;
      e.preventDefault();
      e.stopPropagation();
      const { orden: o, necesita: n, abierto: a, alAbrir: abrir } = ultimo.current;
      const paso = e.key === 'ArrowDown' ? 1 : -1;
      const filtro = useOficinaStore.getState().filtroGrupo;
      if (!a && filtro && paso === 1 && !e.shiftKey) {
        const fuera = o.find((c) => n(c) && !enFiltro(c, filtro));
        if (fuera) {
          useOficinaStore.getState().setFiltroGrupo(null);
          abrir(fuera.agentId);
          return;
        }
      }
      const sig = siguienteEnEquipo(o, a, paso, e.shiftKey ? () => true : n);
      if (sig) abrir(sig.agentId);
      else useToastStore.getState().push({ message: t('oficinaV2.equipo.nadaMasTeToca'), durationMs: 2_500 });
    };
    window.addEventListener('keydown', tecla, true);
    return () => window.removeEventListener('keydown', tecla, true);
  }, []);
}
